"use client";

import React from "react";
import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import Chip from "@/components/global/ui/Chip";
import Image from "@/helpers/Image";
import type { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

const TRANSITION_EASING = [0.22, 1, 0.36, 1] as const;
const TRANSITION_DURATION = 0.6;
const VIEWPORT = { once: true, margin: "-50px" } as const;
const REVEAL_CHIP = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_TITLE = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_ITEM = {
  initial: { opacity: 0, y: 60 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const CHIP_TRANSITION = { duration: 0.7, ease: [...TRANSITION_EASING] };
const TITLE_TRANSITION = { duration: 0.8, ease: [...TRANSITION_EASING], delay: 0.1 };
const ITEM_TRANSITION_BASE = { duration: TRANSITION_DURATION, ease: [...TRANSITION_EASING] };

function ProblemSectionDesktop({ data }: { data: PageHomeSchema }) {
  const { problemChip, problemTitle, problemItems } = data;
  return (
    <section
      className="relative z-[1] hidden px-[3rem] pt-[7rem] lg:block lg:px-0 lg:py-[20rem]"
      id="problem">
      <div className="mx-auto flex w-[min(102rem,100%)] flex-col items-start gap-[4rem] lg:items-center">
        {problemChip && (
          <motion.div
            {...REVEAL_CHIP}
            className="lg:mx-auto"
            transition={CHIP_TRANSITION}>
            <Chip
              text={problemChip}
              animatedIcon
              spinnerSize={32}
              spinnerVariant="loop"
            />
          </motion.div>
        )}

        {problemTitle && (
          <motion.div
            className="richtext richtext-start lg:richtext-center prose-h1:bg-gradient-to-b prose-h1:from-black prose-h1:to-[#353535] prose-h1:bg-clip-text prose-h1:tracking-normal prose-h1:text-transparent"
            {...REVEAL_TITLE}
            transition={TITLE_TRANSITION}>
            <PortableText
              value={problemTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}

        {problemItems && problemItems.length > 0 && (
          <div className="mx-auto flex w-[min(79.625rem,100%)] flex-col gap-[3rem]">
            {problemItems.map((item, index) => (
              <motion.div
                key={index}
                {...REVEAL_ITEM}
                transition={{ ...ITEM_TRANSITION_BASE, delay: index * 0.1 }}
                className="flex flex-col">
                <div className="flex w-fit gap-[6rem] bg-black px-[1rem] py-[0.25rem] [&_*]:m-0 [&_*]:font-spacemono [&_*]:text-[1.75rem] [&_*]:leading-[140%] [&_*]:text-white [&_*]:uppercase">
                  <p>0{index + 1}</p>
                  <h3 className="hidden lg:block">{item.title}</h3>
                </div>
                <div className="flex flex-col border border-black/30 bg-[#F2F2F2] p-[2rem] lg:flex-row lg:items-center lg:p-[1rem]">
                  <div className="mb-[2rem] h-[7rem] w-[7rem] shrink-0 bg-[#D9D9D9] lg:mb-0 lg:h-[15rem] lg:w-[15rem]">
                    {item.image && (
                      <Image
                        className="w-full shrink-0 rounded-[0.75rem] lg:w-[40%]"
                        src={item.image}
                        alt={item.title}
                      />
                    )}
                  </div>

                  <div className="lg:px-[3rem]">
                    {item.title && (
                      <span className="mb-[1rem] text-[2.5rem] leading-[150%] font-medium text-black lg:hidden">
                        {item.title}
                      </span>
                    )}
                    {item.text && (
                      <div className="richtext richtext-start prose-p:m-0 prose-p:text-[2rem] prose-p:leading-[150%] prose-p:text-black lg:prose-p:text-[2.25rem]">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ProblemSectionMobile({ data }: { data: PageHomeSchema }) {
  const { problemChip, problemTitle, problemItems } = data;
  return (
    <section
      className="relative z-[1] px-[3rem] py-[7rem] lg:hidden"
      id="problem">
      <div className="mx-auto flex w-[min(102rem,100%)] flex-col items-start gap-[4rem]">
        {problemChip && (
          <div>
            <Chip
              text={problemChip}
              spinnerSize={32}
              spinnerVariant="loop"
            />
          </div>
        )}

        {problemTitle && (
          <div className="richtext richtext-start prose-h1:bg-gradient-to-b prose-h1:from-black prose-h1:to-[#353535] prose-h1:bg-clip-text prose-h1:tracking-normal prose-h1:text-transparent">
            <PortableText
              value={problemTitle}
              components={richTextComponent}
            />
          </div>
        )}

        {problemItems && problemItems.length > 0 && (
          <div className="mx-auto flex w-[min(79.625rem,100%)] flex-col gap-[3rem]">
            {problemItems.map((item, index) => (
              <div
                key={index}
                className="flex flex-col">
                <div className="flex w-fit gap-[6rem] bg-black px-[1rem] py-[0.25rem] [&_*]:m-0 [&_*]:font-spacemono [&_*]:text-[1.75rem] [&_*]:leading-[140%] [&_*]:text-white [&_*]:uppercase">
                  <p>0{index + 1}</p>
                </div>
                <div className="flex flex-col border border-black/30 bg-[#F2F2F2] p-[2rem]">
                  <div className="mb-[2rem] h-[7rem] w-[7rem] shrink-0 bg-[#D9D9D9]">
                    {item.image && (
                      <Image
                        className="w-full shrink-0 rounded-[0.75rem]"
                        src={item.image}
                        alt={item.title}
                      />
                    )}
                  </div>
                  <div>
                    {item.title && (
                      <span className="mb-[1rem] text-[2.5rem] leading-[150%] font-medium text-black">
                        {item.title}
                      </span>
                    )}
                    {item.text && (
                      <div className="richtext richtext-start prose-p:m-0 prose-p:text-[2rem] prose-p:leading-[150%] prose-p:text-black">
                        <PortableText
                          value={item.text}
                          components={richTextComponent}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function ProblemSection({ data }: { data: PageHomeSchema }) {
  return (
    <>
      <ProblemSectionDesktop data={data} />
      <ProblemSectionMobile data={data} />
    </>
  );
}
